import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import style from './sendmessage.module.scss';
import TextArea from '../TextArea';
import useMessageInput from './useMessageInput';
import useSendMessage from 'hooks/useSendMessage';
import useIsSendInputDisabled from 'hooks/useIsDisabled';
import useApp from 'hooks/useApp';
import { IAppConfigResponse } from 'store/app/app.state';
import Emoji from './Emoji';
import { DEFAULT_LANGUAGE } from 'translations';
import * as Logger from 'services/logger';
import { turnOffThenSetAriaLiveValue } from 'utils'

interface Props {}

const ACCEPTED_FILE_TYPES: string[] = [
    'csv',
    'doc',
    'docx',
    'jpeg',
    'jpg',
    'pdf',
    'png',
    'ppt',
    'pptx',
    'txt',
    'wav',
    'xls',
    'xlsx',
];

const SendMessage: React.FC<Props> = () => {
    const [disabled, setDisabled] = useState(false);
    const [text, setText] = useMessageInput();
    const [attachments, setAttachments] = useState<File[]>([]);
    const [error, setError] = useState<string>('');
    const sendMessage = useSendMessage();
    const { language, translation, attachmentsConfig, emojisConfig, appTextTranslations, instanceID, contactId, addAttachmentLabel, addEmojiLabel } =
        useApp();
    const attachmentInput = useRef<HTMLInputElement>(null)

    useLayoutEffect(() => {
        //Removing aria-live attribute from chat wrapper helps to ignore reading elements in DOM or escape reading them twice when they are in focus
        turnOffThenSetAriaLiveValue('sa-chat-app')
    }, [])

    useEffect(() => {
        if (!text.trim() && attachments.length <= 0) return setDisabled(true);
        setDisabled(false);
    }, [text, attachments]);

    useEffect(() => {
        setTimeout(() => {
            setError('');
        }, 2500);
    }, [error]);

    const handleUpdateContactAttributes = async () => {
        try{
            const currentAttributes = await window.bchat.getContactAttributes({
                contactId: contactId!,
                instanceId: instanceID
            })
                        
            const currentAttachmentCount = +(currentAttributes?.['sa-message-attachment-count'] ?? '0')
            
            await window.bchat.updateContact({
                attributes: {
                    'sa-message-attachment-count': (currentAttachmentCount + attachments.length ?? 1).toString()
                },
                contactId: contactId!
            })
        }
        catch(error) {
            Logger.error('Error updating attributes:', error);
            await window.bchat.putMetric({ metricName: 'Error/Connect/Attributes' })
            setAttachments(() => []);
        }
    }

    const handleSendClick = async (text: string) => {
        setDisabled(true);
        sendMessage(text, undefined, attachments);

        
        if(attachments.length === 0) return;
        
        handleUpdateContactAttributes();
        setAttachments(() => []);
    };

    const addAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;
        for (const file of files) {
            if (!validateAttachment(file, setError, attachmentsConfig).valid) {
                continue;
            }
            setAttachments(old => [...old, file]);
        }
        if (attachmentInput.current) attachmentInput.current.value=""
        setTimeout(() => {
            document.getElementById(`sa_att_${attachments.length}`)?.scrollIntoView();
        }, 200);
    };

    const isFormEnabled = useIsSendInputDisabled() ? undefined : 'form-enabled';
    const isAttachmentsEnabled = attachmentsConfig?.enabled && attachmentsConfig.acceptedFileTypes?.length > 0;
    const sendText = appTextTranslations!.sendText?.[translation?.language ?? language] ?? 'Send';
    const sendMessageAriaLabel = appTextTranslations!.sendMessage?.[language ?? DEFAULT_LANGUAGE] ?? appTextTranslations!.sendMessage?.[DEFAULT_LANGUAGE];


    const onClickAttachmentUpload = (e: React.KeyboardEvent<HTMLLabelElement>) => {
        if (e.keyCode !== 32) return;
        document.getElementById('attachment-upload')?.click();
    };

    return (
        <div role="region" aria-label={sendMessageAriaLabel} className={isFormEnabled}>
            <div className={style.error}>{error && error}</div>
            <form
                autoComplete="off"
                onSubmit={e => {
                    e.preventDefault();
                    handleSendClick(text);
                }}
                className={style.formWrapper}
            >
                <TextArea
                    sendMessage={sendMessage}
                    value={text}
                    setText={setText}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    setError={setError}
                    title={sendMessageAriaLabel}
                />

                {isAttachmentsEnabled && (
                    <>
                        <label
                            onKeyDown={onClickAttachmentUpload}
                            tabIndex={isFormEnabled ? 0 : -1}
                            className={style.attachmentIcon}
                            htmlFor="attachment-upload"
                            aria-label={addAttachmentLabel}
                            id="attachment-upload-label"
                            role="button"
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 25"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M16.6071 14.6869C16.6071 15.1011 16.2714 15.4369 15.8571 15.4369C15.4429 15.4369 15.1071 15.1011 15.1071 14.6869V9.875C15.1071 8.11117 13.7125 6.6875 12 6.6875C10.2875 6.6875 8.89285 8.11117 8.89285 9.875V16.4375C8.89285 17.4765 9.71184 18.3125 10.7143 18.3125C11.7167 18.3125 12.5357 17.4765 12.5357 16.4375V9.875C12.5357 9.56092 12.2924 9.3125 12 9.3125C11.7076 9.3125 11.4643 9.56092 11.4643 9.875V15.5625C11.4643 15.9767 11.1285 16.3125 10.7143 16.3125C10.3001 16.3125 9.96428 15.9767 9.96428 15.5625V9.875C9.96428 8.73934 10.8722 7.8125 12 7.8125C13.1278 7.8125 14.0357 8.73934 14.0357 9.875V16.4375C14.0357 18.298 12.5522 19.8125 10.7143 19.8125C8.87641 19.8125 7.39285 18.298 7.39285 16.4375V9.875C7.39285 7.28959 9.45204 5.1875 12 5.1875C14.5479 5.1875 16.6071 7.28959 16.6071 9.875V14.6869Z"
                                    fill="#696969"
                                />
                            </svg>
                        </label>

                        {isAttachmentsEnabled && (
                            <input
                                ref={attachmentInput}
                                className={style.hidden}
                                id="attachment-upload"
                                type="file"
                                onChange={addAttachment}
                                multiple
                                aria-labelledby="attachment-upload-label"
                                disabled={!isFormEnabled}
                            />
                        )}
                    </>
                )}

                {emojisConfig?.enabled && (
                    <Emoji
                        onClickEmoji={(emoji: string) => setText(text + emoji)}
                        config={emojisConfig}
                        disabled={!isFormEnabled}
                        addEmojiLabel={addEmojiLabel}
                    />
                )}

                <button disabled={disabled} className={style.sendButton} type="submit" tabIndex={0}>
                    {sendText}
                </button>
            </form>
        </div>
    );
};

export const validateAttachment = (file: File, setError: (value: React.SetStateAction<string>) => void, attachmentsConfig: IAppConfigResponse['attachmentsConfig']): {
    valid: boolean,
} => {
    // file name length
    if (file.name.length > 256) {
        setError('File name too large')
        return {
            valid: false
        }
    }

    //accepted file types
    const fileIsValid = (attachmentsConfig?.acceptedFileTypes || ACCEPTED_FILE_TYPES).includes(file.name.split('.').pop()!)
    if (!fileIsValid) {
        setError('Invalid file type.')
        console.log(`invalid ${file.name.split('.').pop()!} where valid files are:`, attachmentsConfig?.acceptedFileTypes || ACCEPTED_FILE_TYPES);
        return {
            valid: false,
        }
    }

    //file size
    if (file.size > (attachmentsConfig?.maxFileSize ?? 20000000)) {
        setError('File size is too large. Max 20MB.')
        return {
            valid: false,
        }
    }
    return {
        valid: true,
    }
}

export default SendMessage;
